import type { PriceDaily } from "epsm-api-types/models";
import { memo, useRef, useState } from "react";
import { Spinner } from "@/components/Spinner";
import { getDataForOneSectorChart } from "../lib/helpers/getDataForOneSectorChart";
import { useCreateSectorsChart } from "../lib/hooks/useCreateSectorsChart";
import { useFetchSectors } from "../lib/hooks/useFetchSectors";
import { OneSectorChart } from "./components/OneSectorChart";
import "./Sectors.css";
import { useIsOnViewport } from "@/hooks/useIsOnViewport";
import { ErrorMessage } from "@/shared/components/ErrorMessage";
import {
	type InitialRmrData,
	initialRmrPrepared,
	initialStocksPrepared,
} from "../config";
import { getStocksByTimeRange } from "../lib/helpers/getStocksByTimeRange/getStocksByTimeRange";
import { type SectorsChartType, SectorsTimeRange } from "../types";
import { SectorsControls } from "./components/SectorControls";

type Props = {
	timeRangeInDays?: number;
	othersProps?: Record<string, unknown>;
};

export const Sectors: React.FC<Props> = memo(({ othersProps }) => {
	const sectorsRef = useRef<HTMLDivElement>(null);
	const { isOnViewport } = useIsOnViewport(sectorsRef);

	const svgRef = useRef(null);
	const parentRef = useRef<HTMLDivElement>(null);

	const stocksPreparedRef = useRef<Record<SectorsTimeRange, PriceDaily[]>>({
		...initialStocksPrepared,
	});
	const rmrPreparedRef = useRef<InitialRmrData>({
		...initialRmrPrepared,
	});

	const stocksPrepared = stocksPreparedRef.current;
	const rmrPrepared = rmrPreparedRef.current;

	const [chartType, setChartType] = useState<SectorsChartType>("Price");
	const [timeRange, setTimeRange] = useState(SectorsTimeRange.SixtyDays);

	const dataByTimeRange = getStocksByTimeRange(
		chartType === "Price" ? stocksPrepared[timeRange] : rmrPrepared[timeRange],
		timeRange,
	);
	const dataForOneSectorChart = getDataForOneSectorChart(dataByTimeRange);

	const { isLoading, error } = useFetchSectors({
		stocksPrepared:
			chartType === "Price"
				? stocksPrepared[timeRange]
				: rmrPrepared[timeRange],
		timeRange,
		isOnViewport,
		chartType,
	});

	useCreateSectorsChart({
		svgRef,
		stocks: dataByTimeRange,
		timeFormat: timeRange,
		parentWidth: parentRef.current?.clientWidth,
		chartType,
	});

	return (
		<div
			className="h-full flex flex-col"
			data-testid="sectors-component"
			{...othersProps}
			ref={sectorsRef}
		>
			<section className="relative mb-3">
				<h2 className="text-accent-foreground text-lg text-center">
					Sector Rotation
				</h2>
				<div className="absolute left-1/2 translate-x-full top-1/2 -translate-y-1/2 ml-[-40px]">
					<SectorsControls
						timeRange={timeRange}
						chartType={chartType}
						handleValueChange={setChartType}
						setTimeRange={setTimeRange}
						className="inline-flex items-center gap-4"
					/>
				</div>
			</section>

			<section className="flex flex-col gap-5">
				<div className="small-charts flex gap-1">
					{Object.entries(dataForOneSectorChart).map(([ticker, tickerData]) => (
						<OneSectorChart
							key={ticker}
							tickerData={tickerData}
							timeRange={timeRange}
							chartType={chartType}
						/>
					))}
				</div>

				<div
					className="main-chart min-h-[600px] h-full flex flex-col gap-2 align-center justify-center"
					ref={parentRef}
				>
					{isLoading && <Spinner />}

					{error && <div>Error: {error.message}</div>}

					{!isLoading && !error && (
						<div>
							<p className="text-center text-[10px] text-gray">
								Click to select/deselect a sector
							</p>

							<div className="w-full h-full min-h-[600px] flex items-center justify-center">
								{chartType === "Ratio" &&
								timeRange === SectorsTimeRange.FifteenDays ? (
									<ErrorMessage
										message="15-day RMR data is not available for the Ratio chart type"
										messageStyle="text-gold"
									/>
								) : (
									<svg data-testid="sectors-chart" ref={svgRef} />
								)}
							</div>
						</div>
					)}
				</div>
			</section>
		</div>
	);
});

Sectors.displayName = "Sectors";
